import React, { forwardRef } from 'react'
import { useEffect, useMemo, useRef } from 'sb-hooks'
import cx from 'classnames'
import Slider from 'react-slick'

import s from './Carousel.scss'


const defaultOptions = {
  zIndex: 100,
  speed: 300,
  initialSlide: 0,
  slidesToShow: 1,
  slidesToScroll: 1,
  touchThreshold: 5,
  autoplaySpeed: 2000,
  autoplay: false,
  adaptiveHeight: true,
  accessibility: true,
  touchMove: false,
  infinite: false,
  arrows: true,
  swipe: false,
  fade: false,
  dots: false,
  useCSS: true,
  cssEase: 'linear',
}

export type CarouselOptions = {
  className?: string
  slidesToShow?: number,
  slidesToScroll?: number,
  arrows?: boolean,
  dots?: boolean,
  infinite?: boolean,
  speed?: number,
  centerMode?: boolean,
  centerPadding?: string,
  adaptiveHeight?: boolean,
  lazyLoad?: string,
  autoplay?: boolean,
  autoplaySpeed?: number,
  swipe?: boolean,
  fade?: boolean,
  cssEase?: string,
  touchMove?: boolean,
  touchThreshold?: number,
  vertical?: boolean,
  verticalSwiping?: boolean,
  zIndex?: number,
  accessibility?: boolean,
  dotsClass?: string,
}

export type CarouselProps = {
  name?: string,
  className?: string,
  options?: CarouselOptions,
  children: React.ReactNode | React.ReactNodeArray
  afterChange?: (index: number) => void,
  beforeChange?: (currentIndex: number, nextIndex: number) => void,
  onSwipe?: () => void,
  withAdaptiveHeight?: boolean,
  withHumbleArrows?: boolean
}


const Carousel = forwardRef<Slider, CarouselProps>((props, forwardedRef) => {
  const {
    name, className, children, options, afterChange,
    beforeChange, withAdaptiveHeight, withHumbleArrows,
  } = props

  const containerRef = useRef(null)
  const sliderRef = useRef(null)

  useEffect(() => {
    // for debug purpose + for stopping autoplay: window['slick-{name}'].slickPause()
    if (name && typeof window !== 'undefined') {
      window[`slick-${name}`] = sliderRef.current
    }

    // ref
    if (typeof forwardedRef === 'function') {
      forwardedRef(sliderRef.current)
    }
    else if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<Slider>).current = sliderRef.current
    }

    // We should prevent default touch event (scrolling)
    // when user try to use carousel

    // saving ref because it can change somehow before events removing
    const containerNode = containerRef.current

    // minimal x delta to start preventing events
    const minDelta = 20

    // we should fix one of the axis after moving
    let fixedAxis
    let firstClientX
    let firstClientY

    const touchStart = (event) => {
      if (event.touches && event.touches[0]) {
        firstClientX = event.touches[0].clientX
        firstClientY = event.touches[0].clientY
      }
      else {
        firstClientX = event.clientX
        firstClientY = event.clientY
      }

      fixedAxis = undefined
    }

    const touchMove = (event) => {
      // Prevent one time until cancel or end
      if (!fixedAxis) {
        let clientX
        let clientY

        /* eslint-disable prefer-destructuring */
        if (event.touches && event.touches[0]) {
          clientX = event.touches[0].clientX
          clientY = event.touches[0].clientY
        }
        else {
          clientX = event.clientX
          clientY = event.clientY
        }
        /* eslint-enable prefer-destructuring */

        const deltaX = clientX - firstClientX
        const deltaY = clientY - firstClientY

        // Prevent scroll if user moved more than minXDelta
        if (Math.abs(deltaX) >= minDelta) {
          fixedAxis = 'x'
        }
        else if (Math.abs(deltaY) >= minDelta) {
          fixedAxis = 'y'
        }
      }

      if (fixedAxis === 'x' && event.cancelable) {
        event.preventDefault()
        event.returnValue = false
      }
    }

    const touchCancel = (event) => {
      fixedAxis = undefined
      firstClientX = undefined
      firstClientY = undefined
    }

    containerNode.addEventListener('touchstart', touchStart)
    containerNode.addEventListener('touchmove', touchMove, { passive: false })
    containerNode.addEventListener('touchcancel', touchCancel)
    containerNode.addEventListener('touchend', touchCancel)

    return () => {
      containerNode.removeEventListener('touchstart', touchStart)
      containerNode.removeEventListener('touchmove', touchMove, { passive: false })
      containerNode.removeEventListener('touchcancel', touchCancel)
      containerNode.removeEventListener('touchend', touchCancel)
    }
  }, [ containerRef, name, forwardedRef ])

  const mergedOptions = useMemo(() => ({
    ...defaultOptions,
    ...options,
  }), [ options ])

  const rootClassName = cx(className, s.carousel, {
    [s.withArrows]: mergedOptions.arrows,
    [s.withAdaptiveHeight]: withAdaptiveHeight,
    [s.withHumbleArrows]: withHumbleArrows,
  })

  return (
    <div className={rootClassName} ref={containerRef}>
      <Slider
        afterChange={afterChange}
        beforeChange={beforeChange}
        {...mergedOptions}
        ref={sliderRef}
      >
        {children}
      </Slider>
    </div>
  )
})


export default Carousel
